import { useEffect, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../../components/ui/dialog";
import { Badge } from "../../components/ui/badge";
import { Textarea } from "../../components/ui/textarea";
import {
  Ban,
  Clock,
  LogIn,
  LogOut,
  Pencil,
  QrCode,
  Trash2,
  UserRound,
} from "lucide-react";
import { toast } from "sonner";
import {
  cancelVisitor,
  checkInVisitor,
  checkOutVisitor,
  deleteVisitor,
  getVisitor,
  type Visitor,
} from "../../api/visitors";
import {
  STATUS_BADGE,
  STATUS_LABEL,
  errMsg,
  fmtClock,
  fmtDate,
  fmtDateTime,
  initials,
  visitDuration,
} from "./visitorUi";

/**
 * One visit in full: who, when, the entry/exit stamps, and the complete scan
 * log — including the refused scans, which is usually what someone is asking
 * about when they open this.
 */
export default function VisitorDetailDialog({
  visitorId,
  canManage,
  onClose,
  onChanged,
  onEdit,
  onShowPass,
}: {
  visitorId: string | null;
  canManage: boolean;
  onClose: () => void;
  onChanged: () => void;
  onEdit: (visitor: Visitor) => void;
  onShowPass: (visitorId: string) => void;
}) {
  const [visitor, setVisitor] = useState<Visitor | null>(null);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [cancelling, setCancelling] = useState(false);
  const [reason, setReason] = useState("");

  const load = async (id: string) => {
    setLoading(true);
    try {
      setVisitor(await getVisitor(id));
    } catch (e) {
      toast.error(errMsg(e, "Could not load this visit"));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!visitorId) {
      setVisitor(null);
      setCancelling(false);
      setReason("");
      return;
    }
    load(visitorId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visitorId]);

  const act = async (fn: () => Promise<Visitor>, done: string) => {
    setBusy(true);
    try {
      setVisitor(await fn());
      toast.success(done);
      onChanged();
    } catch (e) {
      toast.error(errMsg(e, "That action was refused"));
    } finally {
      setBusy(false);
    }
  };

  const remove = async () => {
    if (!visitor) return;
    setBusy(true);
    try {
      await deleteVisitor(visitor.id);
      toast.success("Visit record removed");
      onChanged();
      onClose();
    } catch (e) {
      toast.error(errMsg(e, "Could not remove this record"));
    } finally {
      setBusy(false);
    }
  };

  const duration = visitor ? visitDuration(visitor) : null;

  return (
    <Dialog open={!!visitorId} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <UserRound size={18} /> Visit details
          </DialogTitle>
          <DialogDescription>
            The visit, its entry and exit times, and every scan of its pass.
          </DialogDescription>
        </DialogHeader>

        {loading ? (
          <div className="flex items-center justify-center py-16">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-emerald-600" />
          </div>
        ) : !visitor ? (
          <div className="py-12 text-center text-sm text-gray-500">
            This visit is not available.
          </div>
        ) : (
          <div className="space-y-5">
            {/* --------------------------------------------------- identity */}
            <div className="flex items-start gap-3">
              {visitor.photo_url ? (
                <img
                  src={visitor.photo_url}
                  alt=""
                  className="h-14 w-14 rounded-full bg-gray-100 object-cover"
                />
              ) : (
                <div className="flex h-14 w-14 items-center justify-center rounded-full bg-gray-100 text-base font-semibold text-gray-600">
                  {initials(visitor.visitor_name)}
                </div>
              )}
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2">
                  <span className="truncate text-lg font-semibold text-gray-900">
                    {visitor.visitor_name}
                  </span>
                  <Badge className={`rounded-md ${STATUS_BADGE[visitor.status]}`}>
                    {STATUS_LABEL[visitor.status]}
                  </Badge>
                </div>
                <div className="text-sm text-gray-600">
                  {visitor.company_name || "—"}
                  {visitor.visitor_count > 1 &&
                    ` · party of ${visitor.visitor_count}`}
                </div>
                <div className="text-sm text-gray-600">
                  {[visitor.visitor_phone, visitor.visitor_email]
                    .filter(Boolean)
                    .join(" · ") || "No contact details"}
                </div>
              </div>
            </div>

            {/* ------------------------------------------------------ facts */}
            <div className="grid grid-cols-1 gap-x-6 gap-y-2 sm:grid-cols-2 text-sm">
              {[
                ["Host", visitor.host_name || "—"],
                ["Department", visitor.host_department || "—"],
                ["Purpose", visitor.purpose || "—"],
                [
                  "Scheduled",
                  `${fmtDate(visitor.visit_date)} · ${fmtClock(visitor.expected_time)}`,
                ],
                [
                  "ID proof",
                  visitor.id_proof_type
                    ? `${visitor.id_proof_type} ${visitor.id_proof_number || ""}`.trim()
                    : "—",
                ],
                ["Pass valid until", fmtDateTime(visitor.valid_until)],
                [
                  "Entry",
                  visitor.checked_in_at
                    ? `${fmtDateTime(visitor.checked_in_at)} (${
                        visitor.check_in_method === "MANUAL" ? "manual" : "QR"
                      })`
                    : "—",
                ],
                [
                  "Exit",
                  visitor.checked_out_at
                    ? `${fmtDateTime(visitor.checked_out_at)} (${
                        visitor.check_out_method === "MANUAL" ? "manual" : "QR"
                      })`
                    : "—",
                ],
                ["Time on site", duration || "—"],
                ["Invited by", visitor.created_by_name || "—"],
              ].map(([label, value]) => (
                <div key={label as string} className="flex justify-between gap-3">
                  <span className="text-gray-500">{label}</span>
                  <span className="text-right text-gray-900">{value}</span>
                </div>
              ))}
            </div>

            {visitor.notes && (
              <div className="rounded-lg bg-gray-50 p-3 text-sm text-gray-700 whitespace-pre-wrap">
                {visitor.notes}
              </div>
            )}

            {visitor.status === "CANCELLED" && visitor.cancel_reason && (
              <div className="rounded-lg bg-red-50 p-3 text-sm text-red-800">
                Cancelled: {visitor.cancel_reason}
              </div>
            )}

            {/* -------------------------------------------------- scan log */}
            <div>
              <div className="mb-2 flex items-center gap-1.5 text-sm font-medium text-gray-900">
                <Clock size={14} /> Scan history
              </div>
              {!visitor.scan_logs?.length ? (
                <div className="rounded-lg border border-dashed border-gray-200 py-6 text-center text-xs text-gray-500">
                  This pass has not been presented yet.
                </div>
              ) : (
                <ul className="divide-y divide-gray-100 text-sm">
                  {visitor.scan_logs.map((log) => (
                    <li key={log.id} className="flex items-start gap-3 py-2">
                      <span
                        className={`mt-1.5 h-2 w-2 shrink-0 rounded-full ${
                          log.result === "OK" ? "bg-green-500" : "bg-red-400"
                        }`}
                      />
                      <div className="min-w-0 flex-1">
                        <div className="text-gray-900">
                          {log.action === "CHECK_IN"
                            ? "Check in"
                            : log.action === "CHECK_OUT"
                              ? "Check out"
                              : "Verified"}
                          {log.result !== "OK" && (
                            <span className="text-red-600"> · refused</span>
                          )}
                          <span className="text-xs text-gray-500">
                            {" "}
                            ({log.method === "MANUAL" ? "manual" : "QR"})
                          </span>
                        </div>
                        {log.detail && (
                          <div className="text-xs text-gray-600">{log.detail}</div>
                        )}
                        <div className="text-xs text-gray-400">
                          {fmtDateTime(log.scanned_at)}
                          {log.scanned_by_name ? ` · ${log.scanned_by_name}` : ""}
                        </div>
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </div>

            {cancelling && (
              <div className="rounded-lg border border-amber-200 bg-amber-50 p-3">
                <Textarea
                  rows={2}
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                  placeholder="Why is this visit being cancelled? (optional)"
                />
                <div className="mt-2 flex justify-end gap-2">
                  <button
                    className="gt-btn is-ghost is-sm"
                    onClick={() => setCancelling(false)}
                  >
                    Keep it
                  </button>
                  <button
                    className="gt-btn is-sm"
                    disabled={busy}
                    onClick={() =>
                      act(
                        () => cancelVisitor(visitor.id, reason),
                        "Visit cancelled"
                      ).then(() => setCancelling(false))
                    }
                  >
                    Cancel the visit
                  </button>
                </div>
              </div>
            )}
          </div>
        )}

        {visitor && (
          <DialogFooter className="flex-wrap gap-2">
            <button
              className="gt-btn is-ghost"
              onClick={() => onShowPass(visitor.id)}
            >
              <QrCode size={14} /> Pass
            </button>

            {visitor.status === "INVITED" && (
              <>
                <button className="gt-btn is-ghost" onClick={() => onEdit(visitor)}>
                  <Pencil size={14} /> Edit
                </button>
                {!cancelling && (
                  <button
                    className="gt-btn is-ghost"
                    onClick={() => setCancelling(true)}
                  >
                    <Ban size={14} /> Cancel visit
                  </button>
                )}
              </>
            )}

            {canManage && visitor.status === "INVITED" && (
              <button
                className="gt-btn is-primary"
                disabled={busy}
                onClick={() =>
                  act(() => checkInVisitor(visitor.id), "Checked in")
                }
              >
                <LogIn size={14} /> Check in
              </button>
            )}

            {canManage && visitor.status === "CHECKED_IN" && (
              <button
                className="gt-btn is-primary"
                disabled={busy}
                onClick={() =>
                  act(() => checkOutVisitor(visitor.id), "Checked out")
                }
              >
                <LogOut size={14} /> Check out
              </button>
            )}

            {/* A visit that actually happened is an entry record — the backend
                refuses to delete it, so the button is not offered either. */}
            {canManage && !visitor.checked_in_at && (
              <button className="gt-btn is-ghost" disabled={busy} onClick={remove}>
                <Trash2 size={14} /> Remove
              </button>
            )}
          </DialogFooter>
        )}
      </DialogContent>
    </Dialog>
  );
}
